import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ticket, Plus, Users, Send, TrendingUp, Database, Megaphone, Pencil, Sparkles, ArrowRight } from "lucide-react";
import DashboardShell from "@/components/hola/DashboardShell";
import CouponCard from "@/components/hola/CouponCard";
import CouponEditor from "@/components/hola/CouponEditor";
import MerchantCouponQR from "@/components/hola/MerchantCouponQR";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

export default function CouponsPage() {
  return <DashboardShell active="cupones">{({ merchant }) => <Body merchant={merchant} />}</DashboardShell>;
}

function Body({ merchant }) {
  const [coupons, setCoupons] = useState([]);
  const [quota, setQuota] = useState(null);
  const [audience, setAudience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | coupon

  const load = async () => {
    try {
      const [c, a] = await Promise.all([api.get("/merchants/me/coupons"), api.get("/merchants/me/audience")]);
      setCoupons(c.data.coupons); setQuota(c.data.quota); setAudience(a.data.stats);
    } catch { toast.error("No pude cargar los cupones"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totals = coupons.reduce((acc, c) => ({ sent: acc.sent + c.stats.sent, redeemed: acc.redeemed + c.stats.redeemed, ticket: acc.ticket + c.stats.ticket_total }), { sent: 0, redeemed: 0, ticket: 0 });

  if (loading) return <div className="text-sm text-neutral-500">Cargando…</div>;

  return (
    <div className="space-y-8 max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold flex items-center gap-1"><Ticket className="w-3 h-3" /> Marketing directo</p>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Cupones de descuento</h1>
        </div>
        <Button onClick={() => setEditing("new")} className="rounded-full bg-[#FF007A] hover:bg-[#e6006e] font-bold h-11 px-5" data-testid="coupon-new-btn">
          <Plus className="w-4 h-4" /> Crear cupón
        </Button>
      </div>

      {/* Explicador */}
      <div className="rounded-3xl bg-gradient-to-br from-[#FF007A] via-[#c8107f] to-[#0055FF] text-white p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-center" data-testid="coupon-explainer">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">¿Para qué sirven los cupones?</div>
          <h2 className="font-heading font-extrabold text-2xl md:text-3xl mt-1 leading-tight">Tu propia base de clientes. Tus propias promociones. Cuando tú quieras.</h2>
          <p className="text-sm md:text-base text-white/90 mt-3 leading-relaxed max-w-2xl">
            Cuando un cliente escanea <strong>tu QR personalizado</strong>, queda registrado en una base de datos que es <strong>100% tuya</strong> y recibe al instante un cupón de descuento para su próxima compra.
            A partir de ahí puedes enviarle nuevos cupones cada vez que quieras mover inventario, llenar un día flojo o premiar a tus clientes frecuentes — <strong>sin pagar publicidad</strong>, directo a su Cuponera en el celular.
          </p>
          <div className="mt-4 grid sm:grid-cols-3 gap-2 text-xs">
            <Step n="1" t="Imprime tu QR" d="Pégalo en caja, mesas o bolsas" />
            <Step n="2" t="Diseña cupones con IA" d="Producto, descuento, vigencia y condiciones" />
            <Step n="3" t="Lanza campañas" d="A toda tu base o por segmentos, con su nombre" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 min-w-[220px]">
          <Kpi icon={Database} label="Clientes en tu base" value={audience?.total ?? 0} testid="kpi-audience" />
          <Kpi icon={Send} label="Cupones enviados" value={totals.sent} testid="kpi-sent" />
          <Kpi icon={TrendingUp} label="Canjeados" value={totals.redeemed} testid="kpi-redeemed" />
          <Kpi icon={Users} label="Venta generada" value={`$${Math.round(totals.ticket).toLocaleString("es-MX")}`} testid="kpi-ticket" />
        </div>
      </div>

      <MerchantCouponQR merchant={merchant} />

      {/* Lista de cupones */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-extrabold text-xl">Mis cupones <span className="text-neutral-400 font-semibold text-base">({coupons.length})</span></h2>
          <span className="text-xs text-neutral-500 font-bold" data-testid="coupon-quota-header">Diseños IA: {quota?.remaining}/{quota?.limit} este mes</span>
        </div>
        {coupons.length === 0 ? (
          <button onClick={() => setEditing("new")} className="w-full rounded-3xl border-2 border-dashed border-neutral-300 hover:border-[#FF007A] hover:bg-[#FFF0F7]/40 transition-colors p-10 text-center" data-testid="coupon-empty-cta">
            <div className="w-14 h-14 rounded-2xl bg-[#FF007A]/10 text-[#FF007A] grid place-items-center mx-auto mb-3"><Sparkles className="w-7 h-7" /></div>
            <div className="font-heading font-extrabold text-lg">Crea tu primer cupón</div>
            <p className="text-sm text-neutral-500 mt-1 max-w-md mx-auto">Tip: márcalo como <strong>bienvenida</strong> para que cada nuevo cliente que escanee tu QR lo reciba automáticamente y tenga una razón para volver.</p>
          </button>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {coupons.map((c) => (
              <div key={c.coupon_id} className="group" data-testid={`coupon-item-${c.coupon_id}`}>
                <CouponCard coupon={c} merchantName={merchant.business_name} compact status={c.expired ? "expired" : undefined} />
                <div className="mt-2 flex items-center justify-between text-[11px] text-neutral-500 px-1">
                  <div className="flex gap-2 flex-wrap">
                    {c.welcome && <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 font-bold">Bienvenida</span>}
                    {!c.active && <span className="rounded-full bg-neutral-200 text-neutral-600 px-2 py-0.5 font-bold">Inactivo</span>}
                    <span>{c.stats.sent} enviados · {c.stats.redeemed} canjeados</span>
                  </div>
                  <button onClick={() => setEditing(c)} className="inline-flex items-center gap-1 font-bold text-[#0055FF]" data-testid={`coupon-edit-${c.coupon_id}`}><Pencil className="w-3 h-3" /> Editar</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Link to="/dashboard/campanas" className="flex items-center gap-4 rounded-3xl border-2 border-[#0055FF]/30 bg-[#0055FF]/5 hover:bg-[#0055FF]/10 transition-colors p-5" data-testid="coupon-go-campaigns">
        <div className="w-12 h-12 rounded-2xl bg-[#0055FF] text-white grid place-items-center shrink-0"><Megaphone className="w-6 h-6" /></div>
        <div className="flex-1">
          <div className="font-heading font-extrabold text-lg">Lanzar una campaña a mi base de datos</div>
          <p className="text-sm text-neutral-600">Elige un cupón, un segmento (todos, con nombre, fieles, nunca han canjeado) y envíalo en un clic.</p>
        </div>
        <ArrowRight className="w-5 h-5 text-[#0055FF]" />
      </Link>

      {editing && (
        <CouponEditor
          merchant={merchant}
          coupon={editing === "new" ? null : editing}
          quota={quota}
          onQuota={setQuota}
          onSaved={load}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function Step({ n, t, d }) {
  return (
    <div className="rounded-2xl bg-white/15 backdrop-blur p-3">
      <div className="font-heading font-black text-lg leading-none">{n}</div>
      <div className="font-bold mt-1">{t}</div>
      <div className="text-white/80 text-[11px]">{d}</div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl bg-white/15 backdrop-blur p-3" data-testid={testid}>
      <Icon className="w-4 h-4 opacity-80" />
      <div className="font-heading font-black text-2xl leading-none mt-2">{value}</div>
      <div className="text-[10px] text-white/80 mt-1 leading-tight">{label}</div>
    </div>
  );
}
